import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import AdminSidebar from "../components/AdminSidebar";
import {
  Mail,
  Eye,
  MousePointer,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Loader2,
  BarChart3,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Inbox,
  Send,
  Calendar,
  Filter,
  Download
} from "lucide-react";

const AdminEmailAnalytics = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [queueStatus, setQueueStatus] = useState(null);
  const [period, setPeriod] = useState(30);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (user?.role !== "admin") {
      navigate("/");
      return;
    }
    fetchData();
  }, [user, period]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, queueRes] = await Promise.all([
        fetch(`${API}/track/stats?days=${period}`, { credentials: "include" }),
        fetch(`${API}/reminders/queue-status`, { credentials: "include" })
      ]);

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }

      if (queueRes.ok) {
        const data = await queueRes.json();
        setQueueStatus(data);
      }
    } catch (error) {
      toast.error("Failed to load email analytics");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success("Data refreshed");
  };

  const processQueue = async () => {
    try {
      const response = await fetch(`${API}/reminders/process-queue`, {
        method: "POST",
        credentials: "include"
      });
      if (response.ok) {
        toast.success("Email queue processing triggered");
        setTimeout(fetchData, 2000);
      }
    } catch (error) {
      toast.error("Failed to process queue");
    }
  };

  // Calculate totals from stats
  const totals = stats?.stats?.reduce((acc, s) => ({
    sent: acc.sent + (s.sent || 0),
    opens: acc.opens + (s.opens || 0),
    clicks: acc.clicks + (s.clicks || 0),
    uniqueOpens: acc.uniqueOpens + (s.unique_opens || 0),
    uniqueClicks: acc.uniqueClicks + (s.unique_clicks || 0)
  }), { sent: 0, opens: 0, clicks: 0, uniqueOpens: 0, uniqueClicks: 0 }) || {};

  const overallOpenRate = totals.sent > 0 ? ((totals.uniqueOpens / totals.sent) * 100).toFixed(1) : 0;
  const overallClickRate = totals.sent > 0 ? ((totals.uniqueClicks / totals.sent) * 100).toFixed(1) : 0;
  const clickToOpenRate = totals.uniqueOpens > 0 ? ((totals.uniqueClicks / totals.uniqueOpens) * 100).toFixed(1) : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-crimson animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      <AdminSidebar />
      
      <main className="lg:ml-64 min-h-screen px-4 sm:px-6 pb-6 pt-20 lg:pt-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold mb-2">
                EMAIL <span className="text-crimson">ANALYTICS</span>
              </h1>
              <p className="text-gray-400">Track email engagement and delivery performance</p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={period}
                onChange={(e) => setPeriod(Number(e.target.value))}
                className="px-3 py-2 bg-obsidian-light border border-[#333] text-white text-sm"
                data-testid="period-select"
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
              </select>
              <Button 
                onClick={handleRefresh} 
                variant="outline" 
                className="border-[#333] text-white"
                disabled={refreshing}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} /> 
                Refresh
              </Button>
            </div>
          </div>

          {/* Overview Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard
              icon={Send}
              label="Emails Sent"
              value={totals.sent.toLocaleString()}
              color="text-blue-400"
              bgColor="bg-blue-500/10"
            />
            <StatCard
              icon={Eye}
              label="Open Rate"
              value={`${overallOpenRate}%`}
              subValue={`${totals.uniqueOpens.toLocaleString()} opens`}
              color="text-green-400"
              bgColor="bg-green-500/10"
              trend={parseFloat(overallOpenRate) > 20 ? "up" : parseFloat(overallOpenRate) > 10 ? "neutral" : "down"}
            />
            <StatCard
              icon={MousePointer}
              label="Click Rate"
              value={`${overallClickRate}%`}
              subValue={`${totals.uniqueClicks.toLocaleString()} clicks`}
              color="text-purple-400"
              bgColor="bg-purple-500/10"
              trend={parseFloat(overallClickRate) > 5 ? "up" : parseFloat(overallClickRate) > 2 ? "neutral" : "down"}
            />
            <StatCard
              icon={TrendingUp}
              label="Click-to-Open"
              value={`${clickToOpenRate}%`}
              subValue="CTR"
              color="text-crimson"
              bgColor="bg-crimson/10"
              trend={parseFloat(clickToOpenRate) > 20 ? "up" : parseFloat(clickToOpenRate) > 10 ? "neutral" : "down"}
            />
          </div>

          {/* Email Queue Status */}
          <motion.div
            className="bg-obsidian-light border border-[#222] p-6 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-bold flex items-center gap-2">
                <Inbox className="w-5 h-5 text-crimson" />
                Email Queue Status
              </h2>
              <Button
                onClick={processQueue}
                size="sm"
                className="bg-crimson hover:bg-crimson-hover"
                data-testid="process-queue-btn"
              >
                Process Queue Now
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-obsidian border border-[#333] text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Clock className="w-5 h-5 text-yellow-400" />
                  <span className="text-2xl font-bold text-white">{queueStatus?.queued || 0}</span>
                </div>
                <span className="text-gray-400 text-sm">Queued</span>
              </div>
              <div className="p-4 bg-obsidian border border-[#333] text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-2xl font-bold text-white">{queueStatus?.sent || 0}</span>
                </div>
                <span className="text-gray-400 text-sm">Sent</span>
              </div>
              <div className="p-4 bg-obsidian border border-[#333] text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <XCircle className="w-5 h-5 text-red-400" />
                  <span className="text-2xl font-bold text-white">{queueStatus?.failed || 0}</span>
                </div>
                <span className="text-gray-400 text-sm">Failed</span>
              </div>
            </div>

            {queueStatus?.next_scheduled && (
              <div className="mt-4 pt-4 border-t border-[#333] flex items-center gap-2 text-gray-400 text-sm">
                <Calendar className="w-4 h-4" />
                <span>Next scheduled: {new Date(queueStatus.next_scheduled).toLocaleString()}</span>
              </div>
            )}
          </motion.div>

          {/* Email Type Breakdown */}
          <motion.div
            className="bg-obsidian-light border border-[#222] p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-lg font-bold flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-crimson" />
                Performance by Email Type
              </h2>
              <span className="text-gray-500 text-sm">
                {stats?.period_days} day period
              </span>
            </div>

            {stats?.stats?.length === 0 ? (
              <div className="text-center py-12">
                <Mail className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-white mb-2">No email data yet</h3>
                <p className="text-gray-500">Email tracking data will appear here once emails are sent with tracking enabled.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {stats?.stats?.map((emailType, index) => (
                  <EmailTypeRow key={emailType.email_type || index} data={emailType} />
                ))}
              </div>
            )}
          </motion.div>

          {/* Benchmarks & Tips */}
          <motion.div
            className="mt-8 bg-gradient-to-r from-crimson/10 to-purple-500/10 border border-crimson/20 p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-crimson" />
              Industry Benchmarks
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <BenchmarkCard
                metric="Open Rate"
                value={overallOpenRate}
                benchmark="20-25%"
                description="Average for recruiting/HR emails"
              />
              <BenchmarkCard
                metric="Click Rate"
                value={overallClickRate}
                benchmark="2-5%"
                description="Average for transactional emails"
              />
              <BenchmarkCard
                metric="Click-to-Open"
                value={clickToOpenRate}
                benchmark="10-15%"
                description="Indicates content relevance"
              />
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, subValue, color, bgColor, trend }) => (
  <motion.div
    className="bg-obsidian-light border border-[#222] p-4"
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
  >
    <div className="flex items-center justify-between mb-2">
      <div className={`p-2 ${bgColor}`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      {trend && (
        <div className={`flex items-center text-xs ${
          trend === 'up' ? 'text-green-400' : trend === 'down' ? 'text-red-400' : 'text-gray-400'
        }`}>
          {trend === 'up' && <TrendingUp className="w-3 h-3 mr-1" />}
          {trend === 'down' && <TrendingDown className="w-3 h-3 mr-1" />}
          {trend === 'up' ? 'Good' : trend === 'down' ? 'Low' : 'Avg'}
        </div>
      )}
    </div>
    <div className="text-2xl font-bold text-white mb-1">{value}</div>
    <div className="text-xs text-gray-500">{subValue || label}</div>
  </motion.div>
);

const EmailTypeRow = ({ data }) => {
  const formatType = (type) => {
    if (!type) return "Unknown";
    return type
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getPerformanceColor = (rate, type) => {
    if (type === "open") {
      if (rate >= 25) return "text-green-400";
      if (rate >= 15) return "text-yellow-400";
      return "text-red-400";
    }
    // Click rate
    if (rate >= 5) return "text-green-400";
    if (rate >= 2) return "text-yellow-400";
    return "text-red-400";
  };

  return (
    <div className="p-4 bg-obsidian border border-[#333] hover:border-crimson/30 transition-colors">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <Mail className="w-5 h-5 text-gray-400" />
          <span className="font-medium text-white">{formatType(data.email_type)}</span>
        </div>
        <span className="text-gray-500 text-sm">{data.sent} sent</span>
      </div>

      <div className="grid grid-cols-4 gap-4 text-sm">
        <div>
          <div className="text-gray-500 mb-1">Opens</div>
          <div className="text-white">{data.unique_opens || 0}</div>
        </div>
        <div>
          <div className="text-gray-500 mb-1">Open Rate</div>
          <div className={getPerformanceColor(data.open_rate, "open")}>
            {data.open_rate || 0}%
          </div>
        </div>
        <div>
          <div className="text-gray-500 mb-1">Clicks</div>
          <div className="text-white">{data.unique_clicks || 0}</div>
        </div>
        <div>
          <div className="text-gray-500 mb-1">Click Rate</div>
          <div className={getPerformanceColor(data.click_rate, "click")}>
            {data.click_rate || 0}%
          </div>
        </div>
      </div>

      {/* Progress bars */}
      <div className="mt-3 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 w-12">Open</span>
          <div className="flex-1 h-2 bg-obsidian-light rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500"
              style={{ width: `${Math.min(data.open_rate || 0, 100)}%` }}
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 w-12">Click</span>
          <div className="flex-1 h-2 bg-obsidian-light rounded-full overflow-hidden">
            <div
              className="h-full bg-purple-500"
              style={{ width: `${Math.min((data.click_rate || 0) * 5, 100)}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const BenchmarkCard = ({ metric, value, benchmark, description }) => {
  const numValue = parseFloat(value);
  const [benchLow, benchHigh] = benchmark.split("-").map((v) => parseFloat(v));
  
  let status = "below";
  let color = "text-red-400";
  
  if (numValue >= benchHigh) {
    status = "above";
    color = "text-green-400";
  } else if (numValue >= benchLow) {
    status = "within";
    color = "text-yellow-400";
  }

  return (
    <div className="p-4 bg-obsidian/50 border border-[#333]">
      <div className="flex items-center justify-between mb-2">
        <span className="text-gray-400 text-sm">{metric}</span>
        <span className={`text-xs px-2 py-0.5 ${
          status === "above" ? "bg-green-500/20 text-green-400" :
          status === "within" ? "bg-yellow-500/20 text-yellow-400" :
          "bg-red-500/20 text-red-400"
        }`}>
          {status === "above" ? "Above" : status === "within" ? "Within" : "Below"} benchmark
        </span>
      </div>
      <div className="flex items-end gap-2 mb-2">
        <span className={`text-2xl font-bold ${color}`}>{value}%</span>
        <span className="text-gray-500 text-sm mb-1">vs {benchmark}%</span>
      </div>
      <p className="text-gray-500 text-xs">{description}</p>
    </div>
  );
};

export default AdminEmailAnalytics;
